import { DashboardLayout } from '@/components/dashboard-layout';
import { useAuthContext } from '@/contexts/auth-context';
import { WebSocketProvider } from '@/contexts/websocket-provider';
import { SAN_FRANCISCO_HUB_PATH_IDENTIFIER } from '@/lib/constants';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { Suspense, memo } from 'react';
import { KeepAliveOutlet, KeepAliveProvider } from 'tanstack-router-keepalive';

// DECOMMISSION: The app is being wound down. Archived deals is the only active
// workflow. Inbox, profile, and deal-detail views (used to open archived deals)
// remain reachable; every other authenticated route redirects to archived deals.
const ALLOWED_PATH_SEGMENTS = [
  '/archived-deals',
  '/deal/',
  '/inbox',
  '/profile',
];

export const Route = createFileRoute('/(authenticated)')({
  beforeLoad: ({ context, location }) => {
    if (!context.auth?.isAuthenticated) {
      throw redirect({
        to: '/auth',
        search: {
          redirect: location.href,
        },
      });
    }

    const isAllowed = ALLOWED_PATH_SEGMENTS.some((segment) =>
      location.pathname.includes(segment),
    );
    if (!isAllowed) {
      throw redirect({
        to: '/$hub/archived-deals',
        params: {
          hub:
            context.auth.user?.current_hub ?? SAN_FRANCISCO_HUB_PATH_IDENTIFIER,
        },
      });
    }
  },
  component: AuthLayout,
});

const MemoDashboardLayout = memo(DashboardLayout);

function AuthLayout() {
  const { user } = useAuthContext();
  if (!user) {
    return null;
  }
  return (
    <div className="min-h-screen bg-app">
      <WebSocketProvider user={user}>
        <MemoDashboardLayout user={user}>
          <KeepAliveProvider>
            <Suspense fallback={<div className="min-h-screen bg-app" />}>
              <KeepAliveOutlet />
            </Suspense>
          </KeepAliveProvider>
        </MemoDashboardLayout>
      </WebSocketProvider>
    </div>
  );
}
